/**
 * Single-source GLTF prop helper for the throne hall. Wraps drei's
 * `useGLTF` + `<Clone>` so multiple instances of the same asset don't share
 * the same scene-graph node (Three.js parents each Object3D to one parent,
 * so re-using a cached scene in two places makes one disappear). Cloning
 * via `<Clone>` is cheap and lets us scatter the same model around the hall.
 *
 * Conventional Poly Haven layout — main `.gltf` references `.bin` and
 * `textures/*.jpg` as siblings, so `useGLTF(url)` resolves them off the
 * gltf URL automatically. All asset folders live under
 * `client/public/models/<slug>/` (see fetch script in repo root, output:
 * `<slug>_1k.gltf` + `<slug>.bin` + `textures/...`).
 */
import { Clone, useGLTF } from "@react-three/drei";

const PRELOADED = new Set<string>();

export interface PropProps {
  url: string;
  position?: [number, number, number];
  rotation?: [number, number, number];
  scale?: number | [number, number, number];
}

export function Prop({
  url,
  position = [0, 0, 0],
  rotation = [0, 0, 0],
  scale = 1,
}: PropProps): JSX.Element {
  const { scene } = useGLTF(url);
  if (!PRELOADED.has(url)) {
    PRELOADED.add(url);
    useGLTF.preload(url);
  }
  return (
    <group position={position} rotation={rotation} scale={scale}>
      <Clone object={scene} castShadow receiveShadow />
    </group>
  );
}
